import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { EmojiUtils } from 'src/utils/emoji'
import { addReaction, deleteReaction } from 'src/client/publication'
import { Tooltip } from 'src/components/tooltip'
import {
  PostReactionsProps,
  ReactionProps,
  ReactionListItem,
  ReactionCode
} from './postReactions.types'
import { Reaction, ReactionCounter, ReactionEmoji, Wrapper } from './postReactions.styles'
import { REACTION_TIMEOUT } from './postReactions.constants'
import { usePostSummaryReactions } from './hooks'

const PostReactions = ({
  postId,
  reactions,
  disabled,
  className,
  onReaction,
  onReactionSuccess,
  onReactionError
}: PostReactionsProps) => {
  const [loading, setLoading] = useState(false)

  const allReactionsCount = useMemo(
    () => reactions?.reduce((acc, reaction) => acc + reaction.count, 0) || 0,
    [reactions]
  )

  const {
    fetchSummaryReactions,
    extractUserNameFromReaction,
    addMyUserNameToReaction,
    removeMyUserNameToReaction
  } = usePostSummaryReactions({ postId, canFetch: allReactionsCount > 0 })

  const normalizeReaction = (reactionCode: ReactionCode) => {
    const reaction = reactions?.find((reaction) => reaction.reactionCode === reactionCode)

    return {
      reactionCode,
      reactionEmoji: EmojiUtils.convertEmojiCodeReceive(reactionCode),
      count: reaction?.count || 0,
      reactedByUser: Boolean(reaction?.reactedByUser)
    }
  }

  const [reactionList, setReactionList] = useState<ReactionListItem[]>([
    normalizeReaction(ReactionCode.READ_HEART),
    normalizeReaction(ReactionCode.CLAPPING_HANDS),
    normalizeReaction(ReactionCode.THUMBS_UP),
    normalizeReaction(ReactionCode.FOLDED_HANDS),
    normalizeReaction(ReactionCode.PARTY_POPPER)
  ])

  const handleDecrementCount = async ({ reactionCode, count }: ReactionProps) => {
    try {
      const newCount = count - 1

      setReactionList((oldReactionList) =>
        oldReactionList.map((reaction) =>
          reaction.reactionCode === reactionCode
            ? { ...reaction, count: newCount, reactedByUser: false }
            : reaction
        )
      )
      removeMyUserNameToReaction(reactionCode)
      onReaction?.({ reactionCode, count: newCount })
      await deleteReaction(postId, reactionCode)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    }
  }

  const handleIncrementCount = async ({ reactionCode, count }: ReactionProps) => {
    try {
      const newCount = count + 1

      setReactionList((oldReactionList) =>
        oldReactionList.map((reaction) =>
          reaction.reactionCode === reactionCode
            ? { ...reaction, count: newCount, reactedByUser: true }
            : reaction
        )
      )
      addMyUserNameToReaction(reactionCode)
      onReaction?.({ reactionCode, count: newCount })
      await addReaction(postId, reactionCode)
      onReactionSuccess?.()
    } catch (error) {
      const err = error as { statusCode: number; message: string }

      onReactionError?.({
        statusCode: err.statusCode,
        errorMessage: err.message
      })
    }
  }

  const handleClick = async (reaction: ReactionProps) => {
    if (loading || disabled) return

    setLoading(true)

    if (reaction.reactedByUser) {
      await handleDecrementCount(reaction)
    } else {
      await handleIncrementCount(reaction)
    }

    setTimeout(() => {
      setLoading(false)
    }, REACTION_TIMEOUT)
  }

  const handleMouseEnterOnReactionsWrapper = () => {
    fetchSummaryReactions()
  }

  return (
    <Wrapper
      className={clsx(className, '_d-flex _align-items-center _flex-wrap')}
      onMouseEnter={handleMouseEnterOnReactionsWrapper}
    >
      {reactionList.map((reaction) => (
        <Tooltip
          title={extractUserNameFromReaction(reaction.reactionCode)}
          position="top"
          key={reaction.reactionCode}
        >
          <Reaction
            className={clsx(
              '_border _rounded-pill _text-1 _d-flex _justify-content-center _align-items-center _overflow-hidden _px-2',
              {
                '_border-gray-200': !reaction.reactedByUser,
                '_border-gray-400': reaction.reactedByUser,
                '_cursor-pointer': !disabled,
                '_cursor-default': disabled
              }
            )}
            onClick={() => handleClick(reaction)}
          >
            <ReactionEmoji
              className="_h-full _d-flex _justify-content-center _align-items-center _position-relative _text-1"
              $reactionEmoji={reaction.reactionEmoji}
              $decrement={!reaction.reactedByUser}
            >
              {reaction.reactionEmoji}
            </ReactionEmoji>

            <ReactionCounter className="_text-gray-900 _ml-2">{reaction.count}</ReactionCounter>
          </Reaction>
        </Tooltip>
      ))}
    </Wrapper>
  )
}

export default PostReactions
